/**
 * Presentation rules for CMMD's metered plan allowances.
 *
 * The billing context reports raw meter values in whatever unit the meter is
 * stored in — cents, hours, tokens, message counts. Rendering those side by
 * side without conversion puts `12345` (cents) next to `12345` (tokens) and
 * invites the reader to compare them. Every value on the CMMD plan card goes
 * through {@link formatMeterValue}.
 *
 * @module components/usage/billingMeters
 */
import type { CmmdBillingContext, CmmdBillingSeriesMeterKey } from "@cmmd-forge/contracts";
import { formatCount, formatTokens, formatUsd } from "@cmmd-forge/shared/usageFormat";

/** How a meter's raw number becomes a figure someone can read. */
type MeterUnit = "cents" | "tokens" | "hours" | "count";

interface MeterPresentation {
  readonly label: string;
  readonly unit: MeterUnit;
}

/**
 * The allowances the plan card shows, in display order.
 *
 * Order is declared here rather than taken from the billing response, so the
 * rows do not reshuffle when a plan happens to carry a different subset. The
 * keys mirror `FORGE_BILLING_SERIES_METER_KEYS` in CMMD; the generated
 * `CmmdBillingSeriesMeterKey` union is what keeps the two from drifting.
 */
const PLAN_METERS: Readonly<Record<CmmdBillingSeriesMeterKey, MeterPresentation>> = {
  "ai.total_tokens": { label: "AI tokens", unit: "tokens" },
  "ai.retail_cents": { label: "AI value", unit: "cents" },
  "ai.provider_cents": { label: "AI provider cost", unit: "cents" },
  "forge.provider_egress_cents": { label: "Forge egress", unit: "cents" },
  "forge.runtime_hours": { label: "Runtime hours", unit: "hours" },
  "sidekick.messages": { label: "Sidekick messages", unit: "count" },
};

const PLAN_METER_KEYS = Object.keys(PLAN_METERS) as readonly CmmdBillingSeriesMeterKey[];

export function formatMeterValue(key: CmmdBillingSeriesMeterKey, value: number): string {
  switch (PLAN_METERS[key].unit) {
    case "cents":
      return formatUsd(value / 100);
    case "tokens":
      return formatTokens(value);
    case "hours":
      return `${Math.round(value * 10) / 10}h`;
    case "count":
      return formatCount(value);
  }
}

export interface BillingMeterRow {
  readonly key: CmmdBillingSeriesMeterKey;
  readonly label: string;
  readonly used: number;
  readonly limit: number | null;
  readonly remaining: number | null;
  /** Consumed fraction in `0..1`, or null when the meter is unlimited. */
  readonly share: number | null;
  readonly overageAllowed: boolean;
  readonly usedLabel: string;
  readonly limitLabel: string | null;
  readonly remainingLabel: string | null;
}

/**
 * Consumed share of an allowance.
 *
 * A zero limit is not a rounding problem, it is a plan with no allowance for
 * this meter: dividing gives Infinity and the bar renders as nothing, which
 * reads as untouched. Anything consumed against a zero allowance is full.
 */
function consumedShare(used: number, limit: number): number {
  if (limit <= 0) return used > 0 ? 1 : 0;
  return Math.min(1, Math.max(0, used / limit));
}

/**
 * One row per plan meter the plan actually carries.
 *
 * A meter the plan does not carry is omitted rather than shown at zero: a zero
 * row claims "you have used none of your allowance", which is a different
 * statement from "this plan has no such allowance".
 */
export function buildMeterRows(billing: CmmdBillingContext): readonly BillingMeterRow[] {
  const rows: BillingMeterRow[] = [];

  for (const key of PLAN_METER_KEYS) {
    const meter = billing.meters[key];
    if (!meter) continue;

    rows.push({
      key,
      label: PLAN_METERS[key].label,
      used: meter.used,
      limit: meter.limit,
      remaining: meter.remaining,
      share: meter.limit === null ? null : consumedShare(meter.used, meter.limit),
      overageAllowed: meter.overageAllowed,
      usedLabel: formatMeterValue(key, meter.used),
      limitLabel: meter.limit === null ? null : formatMeterValue(key, meter.limit),
      // The server's own figure: it accounts for credits and rollovers that
      // used and limit alone do not show.
      remainingLabel: meter.remaining === null ? null : formatMeterValue(key, meter.remaining),
    });
  }

  return rows;
}
